import { runBackground } from '../host/runBackground.js';
import { uploadSessionThumbnail } from './api.js';
import { capturePreviewScreenshot } from './previewScreenshot.js';

export type FavoriteThumbnailCaptureInput = {
  readonly sessionId: string;
  readonly iframe: HTMLIFrameElement | null;
  readonly previewUrl: string | undefined;
  readonly captureRecommended: boolean;
  readonly debounceMs?: number;
};

type CaptureSchedulerState = {
  readonly timer: number | undefined;
  readonly inFlight: boolean;
  readonly queued: boolean;
};

const schedulers = new Map<string, CaptureSchedulerState>();

export function shouldScheduleFavoriteThumbnailCapture(input: {
  readonly captureRecommended: boolean;
  readonly previewUrl: string | undefined;
  readonly hasIframe: boolean;
}): boolean {
  return (
    input.captureRecommended &&
    input.previewUrl !== undefined &&
    input.previewUrl.trim() !== '' &&
    input.hasIframe
  );
}

/**
 * Debounced shared-thumbnail capture for a favorited session (or any open viewer
 * when capture is recommended). Failures are swallowed.
 */
export function scheduleFavoriteThumbnailCapture(input: FavoriteThumbnailCaptureInput): void {
  if (
    !shouldScheduleFavoriteThumbnailCapture({
      captureRecommended: input.captureRecommended,
      previewUrl: input.previewUrl,
      hasIframe: input.iframe !== null,
    })
  ) {
    return;
  }
  const { previewUrl, iframe, sessionId } = input;
  if (previewUrl === undefined || iframe === null) {
    return;
  }
  const previous = schedulers.get(sessionId) ?? {
    timer: undefined,
    inFlight: false,
    queued: false,
  };
  if (previous.timer !== undefined) {
    window.clearTimeout(previous.timer);
  }
  const debounceMs = input.debounceMs ?? 400;
  const timer = window.setTimeout(() => {
    const current = schedulers.get(sessionId);
    if (current === undefined) {
      return;
    }
    schedulers.set(sessionId, { ...current, timer: undefined });
    if (current.inFlight) {
      schedulers.set(sessionId, { ...current, timer: undefined, queued: true });
      return;
    }
    runBackground(runCapture(sessionId, iframe, previewUrl));
  }, debounceMs);
  schedulers.set(sessionId, { ...previous, timer });
}

async function runCapture(
  sessionId: string,
  iframe: HTMLIFrameElement,
  previewUrl: string
): Promise<void> {
  const state = schedulers.get(sessionId);
  if (state === undefined) {
    return;
  }
  schedulers.set(sessionId, { ...state, inFlight: true });
  try {
    const file = await capturePreviewScreenshot({
      iframe,
      previewUrl,
      mode: 'viewport',
    });
    await uploadSessionThumbnail(sessionId, file);
  } catch {
    // Non-blocking: pin/favorites remain valid without a fresh thumbnail.
  } finally {
    const latest = schedulers.get(sessionId);
    if (latest !== undefined) {
      schedulers.set(sessionId, { ...latest, inFlight: false });
      if (latest.queued) {
        schedulers.set(sessionId, { ...latest, inFlight: false, queued: false });
        runBackground(runCapture(sessionId, iframe, previewUrl));
      }
    }
  }
}

/** Test helper: clear debounce / in-flight state. */
export function resetFavoriteThumbnailCaptureSchedulers(): void {
  schedulers.forEach((state) => {
    if (state.timer !== undefined) {
      window.clearTimeout(state.timer);
    }
  });
  schedulers.clear();
}
